import React, { useCallback, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';

import ScorecardNavigation from '../ScorecardNavigation';
import {
  getRecallMetrics,
  getRejectionTypeCounts,
  CoverageSuppression,
  RecallMetricsResponse,
  RecallMetricsBlock,
  RecallSeriesBreak,
  RateStat,
  RejectionTypeCount,
} from '../../../services/scopedReadsService';
import { prettyType, WINDOW_OPTIONS, DEFAULT_WINDOW, WindowDays } from './interactionEvaluationsQueries';
import { describeCoverageRule, isKnownCoverageRule, readCount } from './recallCoverage';

// ============================================================================
// feat-199 — Recall QA metrics panel. TRAP-1 lives here.
//
// **THERE IS NO RECALL PERCENTAGE ON THIS PANEL, AND THAT IS THE FEATURE'S
// HIGHEST-VALUE DECISION, NOT AN OMISSION.** Recall is found ÷ actually-happened,
// and nothing observes the denominator. Any screen printing "Recall: 94%" is
// printing a number whose denominator is "what our second model happened to notice"
// — and it WILL be read as authoritative and quoted to a customer.
//
// AC9 holds BY CONSTRUCTION, not by discipline: every rate arrives as a
// `RateStat` object and there is no code path that renders a value without its
// denominator. design-lead's rule (components.md §EvaluatorStatsPanel, approved
// 2026-07-27): **"a rate whose denominator is a caption will lose the caption"** —
// a muted "over 12 judged" beside "83%" reads as redundancy, and redundancy is what
// a later cleanup deletes. So the denominator is not a caption; it is part of the
// statistic, set in the same weight class, so removing it looks like damage.
// ============================================================================

/**
 * One rate cell, over FOUR MUTUALLY EXCLUSIVE SHAPES and no third path
 * (design-lead's structure). **The fourth is the one that must never render** — a
 * bare rate with no denominator — and it is pinned by a test rather than by
 * inspection, per the `DisagreementChip` regression-guard precedent.
 *
 *   1 · sufficient       → value + "N of M reviewed" + CI
 *   2 · below minimum    → "—" + "N reviewed · need M"
 *   3 · no data          → "—" + "no discards in window"
 *   4 · MUST NEVER RENDER → a value with no denominator beside it
 *
 * Shapes 2 and 3 are DIFFERENT COPY on purpose. "Too few yet" is not "there was
 * nothing to review". Collapsing them re-creates AC10's defect one layer up: two
 * different facts rendering identically.
 *
 * Both `—` states use plain `text-inkMuted` and **never `preview-*`** — that token
 * means *seeded / not-yet-real data*, and an insufficient sample is entirely real
 * data, just too little of it. Reusing it would blur a token whose whole value is
 * meaning one thing.
 */
/** `—` for a count we do not have. Never `0`, which would assert a measurement. */
const countText = (n: number | null): string => (n == null ? '—' : n.toLocaleString());

/**
 * feat-205 AC15 / TRAP-6 — the cutover, rendered as a BREAK.
 *
 * After Part B the overturn rate, review coverage and estimated-wrongly-discarded are
 * computed over the REVIEWABLE population; before it, over ALL rejections. A trend
 * spanning that line is **two different denominators wearing one line**, and the ADR is
 * explicit that this must render as a break rather than a footnote.
 *
 * Placed ABOVE the rates it qualifies, following the `scope_coverage` precedent on this
 * same panel and for the same recorded reason: *"a rate whose denominator is a caption
 * will lose the caption"*. A note under a continuous line loses to the line — the line
 * is the claim, and the reader has already believed it by the time they reach the note.
 *
 * `decisions_recorded_on_now_covered_rows` is rendered as a number and not as prose,
 * because it is what makes the break QUANTIFIED rather than declared: D8 says prior
 * calls stand and are flagged, and this is the flag.
 */
export const SeriesBreak: React.FC<{ seriesBreak: RecallSeriesBreak }> = ({ seriesBreak }) => (
  <div
    data-testid="series-break"
    className="mb-4 rounded-lg border-2 border-danger-line/50 bg-danger-surface p-3"
  >
    <p className="text-sm font-extrabold text-danger-text">
      ⚠ These numbers are not continuous with earlier ones.
    </p>
    <p className="mt-1 text-[11px] leading-relaxed text-ink">
      <span data-testid="series-break-rule" className="font-semibold">
        {seriesBreak.rule}
      </span>{' '}
      changed the population these rates are computed over.{' '}
      <span data-testid="series-break-effect">{seriesBreak.effect}</span>
    </p>
    <p className="mt-2 text-[11px] leading-relaxed text-ink">
      <strong data-testid="series-break-count">
        {seriesBreak.decisions_recorded_on_now_covered_rows.toLocaleString()}
      </strong>{' '}
      verdicts were already recorded on rows this rule now classifies as covered. Those calls
      stand and are not re-queued — they are counted here so an overturn rate spanning the
      cutover can be read with that in view.
    </p>
  </div>
);

/**
 * feat-205 AC13 — suppression by interaction type and by COVERING type, each with its
 * denominator visible (feat-199 AC9 carries over unchanged).
 *
 * **The `by_covering_type` rows do not partition anything, and the note saying so is
 * mandatory.** A candidate covered by several types is counted under each, so the rows
 * sum to MORE than `covered` — `initial_greeting` candidates are covered by `goodbye`
 * 264 times and by another `initial_greeting` 78 times, and a single candidate can be
 * in both. A reader who sums the column builds a denominator that is not a population,
 * and nothing about a tidy table would stop them. So the note renders WITH the rows,
 * not beneath them as a caption, on the same rule as every other denominator here.
 */
/**
 * feat-206 D11 / D-b / AC23 — the zero-length suppressions, in three shapes.
 *
 * **D4 IS RULED (2026-08-10) AND IT SPLIT RATHER THAN FLIPPED.** AC31 measured 605
 * candidates with an empty part set: **364 zero-length**, and **241 positive-length
 * spans sitting inside one long part** (1.28 s–65.74 s, mean 9.97 s, none sub-second).
 * The 241 are real moments that select no part only because their span starts just
 * after a part start rather than on it — so (a) zero-length is SUPPRESSED and (b) stays
 * REVIEWABLE. `reference/MEASUREMENT-2026-08-10-containment.md`.
 *
 * Three consequences, and the third is the one worth reading:
 *
 *  1. **The placement never moved.** (a) is suppressed, so this stays an "of which"
 *     line under the suppression headline, exactly where it started.
 *  2. The field is `suppressed_zero_length` on both endpoints — renamed from
 *     `suppressed_empty_parts`, which stopped describing what it counts.
 *  3. **The copy below was the contingent part, and it came back TRUE.** It counts only
 *     zero-length candidates now, so "a detection with no content" is precisely what it
 *     is about. The string that had to be written last turned out to be the one written
 *     first.
 *
 * **This component still takes `count`/`denominator` rather than a
 * `CoverageSuppression`, and that shape is why the rename cost one prop at one call
 * site instead of a refactor.** Worth keeping: two successive scopings of the D4
 * contingency were both wrong and both underestimated it, and a structural defence paid
 * where a comment claiming provisionality would have paid nothing. Don't wire it into
 * {@link SuppressionBlock}'s internals, and don't thread the field name through it.
 *
 * Unchanged by the ruling, as predicted: the three shapes (`>0` warning / real `0`
 * neutral / absent `—`), `—`-not-`0` for absent, and that the count is never a term in
 * a partition.
 */
export const ZeroLengthNote: React.FC<{ count: number | null; denominator: number }> = ({
  count,
  denominator,
}) => {
  // ABSENT IS NOT ZERO, so the caller branches on `typeof` and hands us `null` — `0`
  // and `undefined` are both falsy and mean opposite things: one says every suppression
  // had a real span behind it, the other says nobody looked.
  if (count == null) {
    return (
      <p
        data-testid="suppressed-zero-length-absent"
        className="mt-2.5 border-l-2 border-softBorder py-2 pl-2.5 text-[11px] leading-relaxed text-inkMuted"
      >
        Zero-length detections suppressed: <strong className="text-ink">—</strong> not reported by
        this backend.
      </p>
    );
  }
  // **REWORDED 2026-08-10 FROM "CONTENT" TO "DURATION". THE OLD WORDING NAMED THE WRONG
  // CAUSE** — and note the correction below, because the obvious diagnosis is wrong.
  //
  // *(Superseded diagnosis, mine, kept so nobody re-derives it: I first recorded that
  // "no transcript in their span at all" described the OTHER half — (b), the 241
  // positive-length spans D4 made reviewable — and was therefore aimed at the wrong
  // population. **That does not survive.** Under containment a part IS its start point
  // (D7 retired; parts have no extent), so BOTH halves select no parts: (a) because the
  // span is degenerate, (b) because no part start falls inside it. The sentence is
  // model-true of both and misleading about both — there is speech at (a)'s instant and
  // speech throughout (b)'s span. Re-pointing it at (b) would not have rescued it.)*
  //
  // **The actual defect is the CAUSE it names, which is what a reader acts on.**
  // *Absence of transcript* reads as a capture problem — a microphone, a stream, a
  // dropout. *Absence of duration* is the annotator emitting extentless detections.
  // Different teams, different remediation. This block is the only user-visible
  // description of a pipeline alarm, so the cause it names is the whole of its value.
  // **A zero-length detection can sit in the middle of dense speech**: it selects no
  // parts because it has no EXTENT, not because there was nothing there to select.
  //
  // **AND THE SENTENCE WAS NOT WRONG WHEN IT WAS WRITTEN — the definition moved under
  // it.** "A detection with no content" was accurate against the pre-split rule, where
  // the suppressed set was "selected no transcript part" as one undifferentiated
  // population. D4's split changed what this block counts; the sentence never changed,
  // and nothing pointed at it. That makes it a different animal from every other defect
  // on this feature, which were false the day they were written and needed only someone
  // to check them. **The question that finds this class is not "did you verify this
  // claim?" but "when a definition moves, which sentences were true only under the old
  // one?"** — and note that `grep` finds the identifier while nothing finds the prose
  // that depended on it. A mechanical rename leaves this copy MORE wrong, not less:
  // "empty parts" was at least ambiguous across the two halves, "zero-length" beside
  // content-language is confidently mismatched.
  //
  // It also now matches the neighbour row's "A detection with no duration" — same
  // population, same phrase, both surfaces. That consistency only became available
  // after the split.
  //
  // Six devices keep a rise from reading as good news. It is NOT a term in the headline
  // triple: that is a partition and this is a subset of one of its terms, so it renders
  // as its own block, below, in warning tokens (feat-174's "attention, not alarm" — not
  // `preview`, which means a decision the system made correctly, and not `danger`,
  // which would claim something is broken for certain). `{n} of {m}`, never a percent —
  // a percent invites a threshold and there is no threshold. The word "covered" never
  // appears in it, because under D4(a) these were suppressed WITHOUT being covered, and
  // calling them covered is exactly the misreading. And the misreading is denied out
  // loud rather than hoped against.
  if (count > 0) {
    return (
      <p
        data-testid="suppressed-zero-length"
        data-shape="warning"
        className="mt-2.5 border-l-2 border-warning-solid bg-warning-surface/55 py-2 pl-2.5 text-[11px] leading-relaxed tabular-nums"
      >
        <strong className="font-extrabold text-ink">
          {count.toLocaleString()} of those {denominator.toLocaleString()} were detections with no
          duration.
        </strong>{' '}
        <span className="block text-ink">
          Start and end at the same instant, so they can contain no transcript at all. Nobody saw
          them — they were hidden from the queue without review. That is a fault in what the
          annotator emitted, not coverage.{' '}
          <strong>If this number rises, that is worse, not better.</strong>
        </span>
      </p>
    );
  }
  // A REAL zero renders, in neutral tokens: it is the only thing that proves the counter
  // is live, and it is the good-news case, so it must not wear the warning treatment.
  return (
    <p
      data-testid="suppressed-zero-length"
      data-shape="zero"
      className="mt-2.5 border-l-2 border-softBorder bg-cream-dark/60 py-2 pl-2.5 text-[11px] leading-relaxed tabular-nums text-inkMuted"
    >
      <strong className="font-bold">
        0 of those {denominator.toLocaleString()} were zero-length detections.
      </strong>{' '}
      Every suppression had a real span behind it.
    </p>
  );
};

export const SuppressionBlock: React.FC<{ suppression: CoverageSuppression }> = ({ suppression }) => {
  const reconciles = suppression.reviewable + suppression.covered === suppression.rejected;
  const gloss = describeCoverageRule(suppression.semantics);
  // Absent is not zero — see {@link ZeroLengthNote}.
  const zeroLength =
    typeof suppression.suppressed_zero_length === 'number' &&
    Number.isFinite(suppression.suppressed_zero_length)
      ? suppression.suppressed_zero_length
      : null;
  return (
    <div data-testid="coverage-suppression" className="mb-6 rounded-xl border border-softBorder bg-white p-4">
      <h2 className="text-lg font-extrabold">Suppressed as already covered</h2>
      <p className="mt-1 text-[11px] leading-relaxed text-inkMuted">
        A rejection whose span is already covered by a retained interaction was a correct machine
        decision about a moment that was never dropped. Those are hidden from the reviewer queue —
        and counted here, because suppression that is not counted is a silent drop.
      </p>
      {/* The denominator is part of the statistic, in the same weight class — removing
          it should look like damage rather than like tidying. */}
      <p data-testid="suppression-headline" className="mt-3 text-sm tabular-nums">
        <strong>{suppression.covered.toLocaleString()}</strong> suppressed ·{' '}
        <strong>{suppression.reviewable.toLocaleString()}</strong> reviewable{' '}
        <span className="text-inkMuted">
          of <strong className="text-ink">{suppression.rejected.toLocaleString()}</strong> rejected
        </span>
        {!reconciles && (
          <span data-testid="suppression-unreconciled" className="ml-2 font-bold text-danger-text">
            ≠ these do not reconcile
          </span>
        )}
      </p>
      {/* One element, one call site — see the relocation note on the component. */}
      <ZeroLengthNote count={zeroLength} denominator={suppression.covered} />
      {/* The rule is printed so the reviewer can audit it — which only works if the
          string is the rule that RAN. It has been wrong once: the wire named
          `half_open` for a period while the 0.5s epsilon was what the predicate
          applied, so the panel would have presented the superseded rule as the audited
          one. The check matches the FAMILY (`transcript_part_*`, and `epsilon_*` still,
          through rollout), never the exact value, because the value is derived from the
          rule and changes legitimately. */}
      <p className="mt-1 text-[11px] text-inkMuted">
        Rule: <span data-testid="suppression-rule" className="font-mono">{suppression.semantics}</span>{' '}
        · retained = <span className="font-mono">{suppression.retained_definition}</span>
      </p>
      {/* The raw token is the audit artifact and stays byte-identical to the wire; it is
          also not, on its own, something to show a human. So the gloss renders beneath
          it in the same weight — NOT as a tooltip, on this panel's own recorded rule
          that a caption gets lost. It is DERIVED from the token family, so it cannot
          drift into describing a rule that is not running, and it renders nothing at all
          for an unrecognised family: describing `frobnicate_v9` would invent a
          mechanism, which is worse than the bare token.

          `text-ink`, one weight up from the `Rule:` line above it and with no bold
          inside it (design, 2026-08-09): the gloss is ALL operative clause, so there is
          no scaffolding to de-emphasise against, and bolding most of an 11px muted line
          is a weak signal. Darker than the token line is also the right ordering — the
          token is the audit artifact, the gloss is the part a human reads. Keeping it
          one whole literal is what lets a test assert the sentence rather than a
          fragment. */}
      {gloss && (
        <p data-testid="suppression-rule-gloss" className="mt-0.5 text-[11px] leading-relaxed text-ink">
          {gloss}
        </p>
      )}
      {!isKnownCoverageRule(suppression.semantics) && (
        <p data-testid="unknown-coverage-rule" className="mt-1 text-[11px] font-semibold text-danger-text">
          This build does not recognise that rule. The numbers above may have been computed
          under a rule this screen cannot describe — check the backend before quoting them.
        </p>
      )}

      <h3 className="mt-4 text-sm font-bold">By covering type</h3>
      <p data-testid="by-covering-type-note" className="mt-0.5 text-[11px] leading-relaxed text-warning-on">
        {suppression.by_covering_type_note}
      </p>
      <table className="mt-2 w-full text-sm" data-testid="by-covering-type-table">
        <thead>
          <tr className="border-b border-softBorder text-left text-[11px] uppercase tracking-wide text-inkMuted">
            <th className="py-2">Covering type</th>
            <th className="py-2 text-right">Candidates covered</th>
            <th className="py-2 text-right">of {suppression.covered.toLocaleString()} suppressed</th>
          </tr>
        </thead>
        <tbody>
          {suppression.by_covering_type.map((row) => (
            <tr key={row.covering_type} className="border-b border-softBorder/50" data-testid="covering-type-row">
              <td className="py-1.5">{prettyType(row.covering_type)}</td>
              <td className="py-1.5 text-right tabular-nums font-semibold">
                {row.candidates_covered.toLocaleString()}
              </td>
              {/* Each row carries its own denominator rather than a bare share, so a
                  row cannot be lifted out of the table and quoted as a fraction of
                  something it was never over. */}
              <td className="py-1.5 text-right tabular-nums text-inkMuted">
                {suppression.covered > 0
                  ? `${Math.round((row.candidates_covered / suppression.covered) * 1000) / 10}%`
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {suppression.by_covering_type.length === 0 && (
        <p className="mt-2 text-[11px] text-inkMuted">No covering types recorded in this window.</p>
      )}
    </div>
  );
};

/**
 * feat-205 AC14 / D8 — how many ALREADY-DECIDED candidates the coverage check now
 * classifies as covered.
 *
 * Prior calls stand and are not re-queued; this is the flag that makes them readable.
 * Counts come from the block's open `counts` map, so each is read through
 * {@link readCount} and renders `—` rather than `0` when absent — see the rationale
 * there. The only guard on these key names lives in backend's test that pins all
 * eighteen by literal name.
 */
export const CoveredDecisionsBlock: React.FC<{ block: RecallMetricsBlock }> = ({ block }) => {
  const reviewed = readCount(block.counts, 'covered_reviewed');
  const upheld = readCount(block.counts, 'covered_upheld');
  const overturned = readCount(block.counts, 'covered_overturned');
  const unsure = readCount(block.counts, 'covered_unsure');
  const covered = readCount(block.counts, 'covered');
  return (
    <div data-testid="covered-decisions" className="mb-6 rounded-xl border border-softBorder bg-white p-4">
      <h2 className="text-lg font-extrabold">Calls already recorded on now-covered rows</h2>
      <p className="mt-1 text-[11px] leading-relaxed text-inkMuted">
        Verdicts recorded before the coverage rule existed, on candidates it would now suppress.{' '}
        <strong className="text-ink">They stand and are not re-queued</strong> (D8) — they are flagged
        here so any overturn rate spanning the cutover can be read with them in view.
      </p>
      <p data-testid="covered-decisions-headline" className="mt-3 text-sm tabular-nums">
        <strong>{countText(reviewed)}</strong> decided{' '}
        <span className="text-inkMuted">
          of <strong className="text-ink">{countText(covered)}</strong> now covered
        </span>
      </p>
      <p className="mt-1 text-[11px] tabular-nums text-inkMuted">
        <span data-testid="covered-upheld">{countText(upheld)}</span> double-check was right ·{' '}
        <span data-testid="covered-overturned">{countText(overturned)}</span> should have been kept ·{' '}
        <span data-testid="covered-unsure">{countText(unsure)}</span> can't tell
      </p>
      {(reviewed == null || covered == null) && (
        <p data-testid="covered-decisions-missing" className="mt-2 text-[11px] font-semibold text-danger-text">
          A “—” means the count did not arrive, not that it is zero.
        </p>
      )}
    </div>
  );
};

export const RateCell: React.FC<{ label: string; rate: RateStat | undefined; minDenominator: number }> = ({
  label,
  rate,
  minDenominator,
}) => {
  if (!rate) {
    return (
      <div className="rounded-xl border border-softBorder bg-white p-4" data-testid="rate-cell" data-shape="absent">
        <p className="text-[10px] font-bold uppercase tracking-wide text-inkMuted">{label}</p>
        <p className="mt-2 text-2xl font-extrabold leading-none text-inkMuted">—</p>
        <p className="mt-1 text-xs font-semibold text-inkMuted">not reported</p>
      </div>
    );
  }

  // Shape 1 — sufficient. The ONLY branch that renders a value, and it renders the
  // denominator in the same breath. There is deliberately no path to a value alone.
  if (rate.value != null) {
    return (
      <div className="rounded-xl border border-softBorder bg-white p-4" data-testid="rate-cell" data-shape="sufficient">
        <p className="text-[10px] font-bold uppercase tracking-wide text-success">{label}</p>
        <p className="mt-2 text-2xl font-extrabold leading-none" data-testid="rate-value">
          {Math.round(rate.value * 100)}%
        </p>
        {/* The denominator is part of the statistic, not a footnote. */}
        <p className="mt-1 text-xs font-semibold text-ink" data-testid="rate-denominator">
          {rate.numerator} of {rate.denominator} reviewed
          {rate.ci_low != null && rate.ci_high != null && (
            <span className="font-normal text-inkMuted">
              {' '}
              · 95% CI {Math.round(rate.ci_low * 100)}–{Math.round(rate.ci_high * 100)}%
            </span>
          )}
        </p>
      </div>
    );
  }

  // Shapes 2 and 3 — both suppressed, DIFFERENT copy. `below_min_denominator` and
  // `no_data` are different facts and one null cannot carry both.
  const belowMin = rate.suppressed_reason === 'below_min_denominator';
  return (
    <div
      className="rounded-xl border border-softBorder bg-white p-4"
      data-testid="rate-cell"
      data-shape={belowMin ? 'below_minimum' : 'no_data'}
      data-suppressed-reason={rate.suppressed_reason ?? 'unknown'}
    >
      <p className="text-[10px] font-bold uppercase tracking-wide text-inkMuted">{label}</p>
      {/* text-inkMuted, NEVER preview-* — see the component docblock. */}
      <p className="mt-2 text-2xl font-extrabold leading-none text-inkMuted">—</p>
      <p className="mt-1 text-xs font-semibold text-inkMuted" data-testid="rate-denominator">
        {belowMin
          ? `${rate.denominator} reviewed · need ${minDenominator}`
          : 'no discards in window'}
      </p>
    </div>
  );
};

/**
 * The recall tile — a tile for a number we deliberately refuse to show.
 *
 * It carries the MECHANISM, not just the refusal. A bare "not computable" is **a
 * claim without a mechanism, and that is what the next confident person overturns**;
 * with the reason attached, overturning it requires producing the denominator, which
 * is the correct bar.
 *
 * And it is drawn at all because an absent tile and a deliberately-withheld one read
 * identically to a newcomer — nothing later contradicts an absence, so someone
 * eventually asks "can we add recall?" and the whole decision is re-litigated from
 * scratch. Stating it converts a silent gap into a recorded one.
 */
const RecallTile: React.FC = () => (
  <div
    className="rounded-xl border border-dashed border-softBorder bg-cream/60 p-5"
    data-testid="recall-tile"
  >
    <p className="text-[11px] font-bold uppercase tracking-wide text-inkMuted">Recall</p>
    <p className="mt-2 text-3xl font-extrabold leading-none text-inkMuted">—</p>
    <p className="mt-1.5 text-sm font-semibold text-inkMuted">not computable</p>
    <p className="mt-2 text-xs font-semibold leading-relaxed text-ink" data-testid="recall-mechanism">
      Recall needs a count of interactions that actually happened. Nothing observes that.
    </p>
  </div>
);

/**
 * The implausibly-fast fraction, and the two numbers without which it is
 * uninterpretable.
 *
 * Its denominator is `latency_known`, NOT `reviewed` — deliberately. Over all
 * decisions the fraction would FALL as the client stopped reporting latency, i.e.
 * **it would look best exactly when the instrument broke.** So `latency_unreported`
 * renders beside it: a fraction over a population nobody can see says nothing.
 *
 * The threshold travels WITH the fraction rather than living only in the backend,
 * because the cutoff is PROVISIONAL by ruling — a guard constant never checked
 * against its own distribution is inert while reading as protection. The buckets are
 * what make it checkable.
 *
 * EVIDENCE, NEVER A GATE. A fast click on an obvious case is legitimate; nothing is
 * excluded on this. Auto-excluding would trade a silent wrong-inclusion for a silent
 * wrong-exclusion — the same defect facing the other way.
 */
const LatencyBlock: React.FC<{ block: RecallMetricsBlock; minDenominator: number }> = ({
  block,
  minDenominator,
}) => {
  // design-lead's ruling: the FRACTION, the THRESHOLD and `latency_unreported` are
  // ONE STATISTIC IN THREE PARTS, and none may render without the other two. A
  // fraction without its cutoff is uninterpretable; without `latency_unreported` it
  // silently claims a denominator it doesn't have. Same shape as the value+denominator
  // rule. **If they can't all render, render NONE and say why** — a partial render
  // here would be a bare rate wearing a different hat.
  const hasFraction = block.implausibly_fast_rate != null;
  const hasThreshold = block.implausibly_fast_threshold_ms != null;
  const hasUnreported = block.latency_unreported != null && block.latency_known != null;
  const complete = hasFraction && hasThreshold && hasUnreported;

  if (!hasFraction && !hasThreshold && !hasUnreported) return null;

  if (!complete) {
    return (
      <div
        className="rounded-xl border border-softBorder bg-cream/40 p-4"
        data-testid="latency-block"
        data-incomplete="true"
      >
        <p className="text-[10px] font-bold uppercase tracking-wide text-inkMuted">
          Decision speed
        </p>
        <p className="mt-2 text-sm font-semibold text-inkMuted" data-testid="latency-incomplete">
          Not shown — this needs the fraction, its cutoff and the count of decisions with
          no timing, and at least one is missing. A fraction without all three would
          overstate what it knows.
        </p>
      </div>
    );
  }

  const buckets = block.latency_buckets;
  return (
    <div className="rounded-xl border border-softBorder bg-white p-4" data-testid="latency-block">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-[10px] font-bold uppercase tracking-wide text-inkMuted">
          Decision speed
        </p>
        {block.implausibly_fast_threshold_ms != null && (
          <p className="text-[11px] text-inkMuted" data-testid="latency-threshold">
            “fast” = under {(block.implausibly_fast_threshold_ms / 1000).toFixed(1)}s
            <span className="ml-1 italic">(provisional)</span>
          </p>
        )}
      </div>
      <div className="mt-2 grid gap-3 sm:grid-cols-2">
        <RateCell
          label="Implausibly fast"
          rate={block.implausibly_fast_rate}
          minDenominator={minDenominator}
        />
        <div className="rounded-xl border border-softBorder bg-cream/40 p-4">
          <p className="text-[10px] font-bold uppercase tracking-wide text-inkMuted">
            What that fraction can't see
          </p>
          {/* Without this the fraction is over a population nobody can see — and it
              would improve as the instrument degraded. */}
          <p className="mt-2 text-sm font-semibold text-ink" data-testid="latency-unreported">
            {block.latency_unreported ?? 0} of {(block.latency_known ?? 0) + (block.latency_unreported ?? 0)}{' '}
            decisions reported no timing
          </p>
        </div>
      </div>
      {buckets && (
        <div className="mt-3 flex flex-wrap gap-2 text-[11px]" data-testid="latency-buckets">
          {(
            [
              ['under_1s', '<1s'],
              ['s1_2', '1–2s'],
              ['s2_5', '2–5s'],
              ['s5_15', '5–15s'],
              ['s15_60', '15–60s'],
              ['over_60s', '>60s'],
            ] as const
          ).map(([key, label]) => (
            <span
              key={key}
              data-testid={`latency-bucket-${key}`}
              className="rounded-full border border-softBorder bg-cream px-2 py-0.5"
            >
              {label} <span className="font-bold">{buckets[key]}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const RecallMetricsPanel: React.FC = () => {
  const [data, setData] = useState<RecallMetricsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [windowDays] = useState<WindowDays>(DEFAULT_WINDOW);
  const [unaccounted, setUnaccounted] = useState<RejectionTypeCount[]>([]);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setData(await getRecallMetrics({ days: windowDays }));
    } catch (e) {
      Sentry.captureException(e);
      // AC10 applies here too — a catch never falls through to a zero-looking panel.
      setError(e instanceof Error ? e.message : 'Failed to load metrics');
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [windowDays]);

  useEffect(() => {
    load();
  }, [load]);

  // The unaccounted-type signal lives in `type-counts`, whose population is
  // deliberately WIDER than this panel's. Fetched independently so a failure here
  // degrades one row rather than the panel.
  useEffect(() => {
    let cancelled = false;
    getRejectionTypeCounts({ days: windowDays })
      .then((r) => {
        if (!cancelled) setUnaccounted((r.counts ?? []).filter((c) => c.state === 'not_in_phase_1_scope'));
      })
      .catch((e) => {
        if (!cancelled) Sentry.captureException(e);
      });
    return () => {
      cancelled = true;
    };
  }, [windowDays]);

  return (
    <div className="min-h-screen-dynamic bg-cream">
      <ScorecardNavigation />
      <div className="mx-auto max-w-5xl px-4 py-6 md:px-8">
        <h1 className="mb-1 text-2xl font-extrabold">Recall QA — metrics panel</h1>
        <p className="mb-4 text-sm text-inkMuted">
          How often the live double-check discarded something a human would have kept. Human
          verdicts are eval metadata — they change no score.
        </p>

        {isLoading ? (
          <div data-testid="metrics-loading" className="rounded-xl border border-softBorder bg-white p-6">
            <p className="sr-only" role="status" aria-live="polite">
              Loading metrics…
            </p>
            <div className="h-16 animate-pulse rounded bg-cream-dark" />
          </div>
        ) : error ? (
          <div
            data-testid="metrics-error"
            role="alert"
            className="rounded-xl border-2 border-danger/30 bg-danger-surface/30 p-6 text-center"
          >
            <p className="text-sm font-extrabold text-danger-text">Couldn't load the metrics</p>
            <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-danger-text/80">
              The request failed, so these numbers are unknown rather than zero.{' '}
              <strong>Do not read a blank panel as "nothing was overturned".</strong>
            </p>
            <p className="mt-3 font-mono text-[11px] text-danger-text/70">{error}</p>
            <button
              type="button"
              onClick={load}
              className="mt-4 rounded-lg bg-danger px-3 py-1.5 text-xs font-bold text-white hover:opacity-90"
            >
              Try again
            </button>
          </div>
        ) : data ? (
          <>
            {/* The population is stated ON the panel, not in a caption — it is what
                makes the rates mean what they say. §5.2b restricts every metric to
                the judged types, because an auto-confirmed row is structurally
                undiscardable and would deflate the discard rate by construction. */}
            {/* §5.2d — coverage is a FIRST-CLASS FACT, rendered above the numbers it
                qualifies rather than as a caption beneath them. The backend's own note:
                "a panel that renders them without that fact is labelling half the
                traffic as all of it." `population` alone says "in-scope types", which
                is true and which a reader cannot decode — it does not tell anyone that
                a deferred type with thousands of rejections is excluded. A rate wearing
                a LABEL instead of a DENOMINATOR is the exact TRAP-1 failure this panel
                exists to prevent. */}
            {/* AC15 first — before the coverage note and before every rate, because it
                is the statement that the numbers below are not continuous with the ones
                the reader remembers. A break placed after the numbers is a footnote. */}
            {data.series_break && <SeriesBreak seriesBreak={data.series_break} />}
            {data.scope_coverage && (
              <div
                className="mb-4 rounded-lg border border-warning-on/40 bg-warning-surface p-3"
                data-testid="scope-coverage"
              >
                <p className="text-sm font-bold text-ink">
                  These numbers cover{' '}
                  <span data-testid="scope-coverage-share">
                    {Math.round(data.scope_coverage.measured_share_of_rejections * 1000) / 10}%
                  </span>{' '}
                  of discarded candidates.
                </p>
                <p className="mt-1 text-[11px] leading-relaxed text-ink">
                  Excluded, and carrying real rejections:{' '}
                  <span className="font-semibold" data-testid="scope-coverage-deferred">
                    {data.scope_coverage.deferred_types.map(prettyType).join(', ')}
                  </span>
                  . {data.scope_coverage.note}
                </p>
              </div>
            )}
            <p className="mb-4 rounded-lg bg-cream-dark/60 p-3 text-[11px] leading-relaxed text-inkMuted" data-testid="population-note">
              <strong className="text-ink">Population:</strong> {data.total?.discard_rate?.population ?? data.population}
              {data.sampling_basis && (
                <>
                  {' '}
                  <span data-testid="sampling-basis">{data.sampling_basis}</span>
                </>
              )}
            </p>

            <div className="mb-6 grid gap-4 md:grid-cols-3">
              <RateCell
                label="Overturn rate"
                rate={data.total?.overturn_rate}
                minDenominator={data.min_denominator}
              />
              <RateCell
                label="Review coverage"
                rate={data.total?.review_coverage}
                minDenominator={data.min_denominator}
              />
              <RecallTile />
            </div>

            {data.total && (
              <div className="mb-6">
                <LatencyBlock block={data.total} minDenominator={data.min_denominator} />
              </div>
            )}

            {/* AC13 / AC14 — the suppression accounting. Above "By type" because the
                per-type rates below are now computed over the REVIEWABLE population,
                and a reader needs to know what was removed before reading them. */}
            {data.coverage_suppression && <SuppressionBlock suppression={data.coverage_suppression} />}
            {data.coverage_suppression && data.total && <CoveredDecisionsBlock block={data.total} />}

            <h2 className="mb-2 text-lg font-extrabold">By type</h2>
            <div className="mb-6 grid gap-4 md:grid-cols-2">
              {(data.by_type ?? []).map((b) => (
                <div key={b.interaction_type} className="rounded-xl border border-softBorder bg-white p-4">
                  <p className="mb-2 text-sm font-bold">{prettyType(b.interaction_type ?? '')}</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <RateCell label="Overturn" rate={b.overturn_rate} minDenominator={data.min_denominator} />
                    <RateCell label="Discard" rate={b.discard_rate} minDenominator={data.min_denominator} />
                  </div>
                </div>
              ))}
            </div>

            {/* AC8 — two prompt versions, comparable. Rendered as separate rows with
                their OWN denominators; a delta across two different denominators and
                two different review sessions is not a comparison, so no delta is
                computed here. */}
            <h2 className="mb-2 text-lg font-extrabold">By prompt version</h2>
            <div className="mb-6 overflow-x-auto">
              <table className="w-full text-sm" data-testid="by-version-table">
                <thead>
                  <tr className="border-b border-softBorder text-left text-[11px] uppercase tracking-wide text-inkMuted">
                    <th className="py-2">Type</th>
                    <th className="py-2">Prompt version</th>
                    <th className="py-2">Overturn</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.by_type_and_version ?? []).map((b, i) => (
                    <tr key={`${b.interaction_type}-${b.prompt_version_id}-${i}`} className="border-b border-softBorder">
                      <td className="py-2">{prettyType(b.interaction_type ?? '')}</td>
                      <td className="py-2 font-mono text-xs">{b.prompt_version_id ?? '—'}</td>
                      <td className="py-2">
                        <RateCell label="" rate={b.overturn_rate} minDenominator={data.min_denominator} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* design-lead's ruling: the unaccounted-type state belongs HERE, not in
                Shane's picker — this is where Preston and Eric can act, and where an
                unenumerated type is a headline rather than an obstacle. It keeps the
                warning token (feat-174: "attention, not alarm" — a review signal): it
                says our model of the type space is incomplete.

                SOURCED FROM `type-counts`, NOT from `recall-metrics`. An earlier
                version of this block read `data.unaccounted_types` — a field I had
                ASSUMED and never verified, and which the backend does not send. It was
                dead code that LOOKED built, which is worse than an absent feature: QA
                found the state was on neither surface. `type-counts` is the endpoint
                whose population is deliberately wider and whose `_state()` actually
                returns `not_in_phase_1_scope`, so it is the one that can answer this. */}
            {unaccounted.length > 0 && (
              <div
                className="mb-6 rounded-xl border border-warning-on/40 bg-warning-surface p-4"
                data-testid="unaccounted-types"
              >
                <p className="text-[11px] font-bold uppercase tracking-wide text-warning-on">
                  A type we haven't accounted for
                </p>
                {unaccounted.map((row) => (
                  <p key={row.interaction_type} className="mt-1 text-sm font-semibold text-ink">
                    <span className="font-mono">{row.interaction_type}</span> —{' '}
                    {row.rejections.toLocaleString()} rejections, in no list this screen knows.
                  </p>
                ))}
              </div>
            )}

            {/* Never 0%, never omitted. A 0% here would read as "never wrongly
                discards" when the truth is "never runs" — a fake number of the same
                species as a fake recall %. */}
            {(data.not_double_checked ?? []).length > 0 && (
              <div className="rounded-xl border border-softBorder bg-cream/40 p-4" data-testid="not-double-checked">
                {data.not_double_checked.map((row) => (
                  <p key={row.interaction_type} className="text-xs text-inkMuted">
                    <strong className="text-ink">{prettyType(row.interaction_type)}</strong> — always
                    auto-confirmed, never discarded. {row.reason}
                  </p>
                ))}
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default RecallMetricsPanel;
